import { Link } from "react-router-dom";

import { Sparkles, Sun, Moon } from "lucide-react";
import { WhatsAppIcon } from "@/components/flowly/WhatsAppCommunity/WhatsAppIcon";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { useFeatureRequestDialog } from "@/contexts/FeatureRequestDialogContext";
import { useAuth } from "@/contexts/AuthContext";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import { LanguageSelector } from "./LanguageSelector";
import { COMMUNITY_ENABLED } from "@/config/featureFlags";
import { useCommunityCta } from "@/components/flowly/WhatsAppCommunity/useCommunityCta";

export function Footer() {
  const { t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { resetConsent } = useCookieConsent();
  const { open: openFeatureRequestDialog } = useFeatureRequestDialog();
  const { user } = useAuth();
  const { getCommunityHref, openCommunity } = useCommunityCta();

  return (
    <footer className="border-t border-border py-20 mt-32 bg-background">
      <PageContainer
        width="wide"
        className="flex flex-col md:flex-row justify-between items-start gap-16"
      >
        <div className="max-w-sm text-start">
          <div className="flex items-center gap-3 mb-6">
            <img src="/logo.png" alt="TodoAI" className="w-6 h-6" />
            <span className="text-2xl font-bold tracking-tight text-foreground">
              TodoAI
            </span>
          </div>
          <p className="text-muted-foreground leading-relaxed mb-8">
            {t.footer.description}
          </p>
          <a
            href="https://todowith.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary hover:underline"
          >
            {t.footer.agencyCta} →
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-16">
          <div className="flex flex-col gap-4 text-start">
            <h5 className="text-foreground font-bold mb-2">
              {t.footer.create}
            </h5>
            <span className="text-muted-foreground/60 text-xs font-medium uppercase tracking-wider">
              {t.footer.images}
            </span>
            <Link
              to="/generate-image"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.imageGenerator}
            </Link>
            <Link
              to="/generate-product-catalog"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.adsCreator}
            </Link>
            <Link
              to="/image-director-chat"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.imageDirector}
            </Link>
            <Link
              to="/profile-photo"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.profilePhoto}
            </Link>
            <span className="text-muted-foreground/60 text-xs font-medium uppercase tracking-wider mt-2">
              {t.footer.videos}
            </span>
            <Link
              to="/frames-to-video"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.framesToVideo}
            </Link>
            <Link
              to="/text-to-video"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.textToVideo}
            </Link>
          </div>
          <div className="flex flex-col gap-4 text-start">
            <h5 className="text-foreground font-bold mb-2">
              {t.footer.discover}
            </h5>
            {COMMUNITY_ENABLED && (
              <Link
                to="/explore"
                className="text-muted-foreground hover:text-foreground text-sm"
              >
                {t.footer.explore}
              </Link>
            )}
            <Link
              to="/pricing"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.pricing}
            </Link>
            <a
              href={getCommunityHref()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => openCommunity("footer")}
              className="text-muted-foreground hover:text-[#25D366] text-sm flex items-center gap-1.5"
            >
              <WhatsAppIcon className="w-3.5 h-3.5 text-[#25D366]" />
              {t.community.footerLink}
            </a>
          </div>
          <div className="flex flex-col gap-4 text-start">
            <h5 className="text-foreground font-bold mb-2">
              {t.footer.support}
            </h5>
            <Link
              to="/help"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.help}
            </Link>
            <Link
              to="/contact"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.contact}
            </Link>
            {/* Submitting requires an authenticated session (POST /api/feature-requests
                refuses an anonymous user). Hide the trigger entirely rather than
                showing a button that 401s on submit. */}
            {user && (
              <button
                type="button"
                onClick={() => openFeatureRequestDialog("footer")}
                className="text-muted-foreground hover:text-foreground text-sm text-start flex items-center gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {t.footer.suggestFeature}
              </button>
            )}
          </div>
          <div className="flex flex-col gap-4 text-start">
            <h5 className="text-foreground font-bold mb-2">{t.footer.legal}</h5>
            <Link
              to="/privacy"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.privacy}
            </Link>
            <Link
              to="/terms"
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              {t.footer.terms}
            </Link>
            <Link
              to="/accessibility"
              className="text-muted-foreground hover:text-foreground text-sm hidden md:block"
            >
              {t.footer.accessibility}
            </Link>
            <button
              onClick={resetConsent}
              className="text-muted-foreground hover:text-foreground text-sm text-start"
            >
              {t.footer.manageCookies}
            </button>
          </div>
        </div>
      </PageContainer>

      <PageContainer
        width="wide"
        className="mt-20 pt-8 border-t border-border text-muted-foreground text-[10px] font-bold uppercase tracking-widest flex flex-col sm:flex-row justify-between items-center gap-4"
      >
        <span>{t.footer.copyright}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="px-3 py-2 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-all"
          >
            {theme === "dark" ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </button>
          <LanguageSelector />
        </div>
      </PageContainer>
    </footer>
  );
}
